import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import DynamicFormWithNestedTabs from "../../components/DynamicForm";
import { generateRequestConfig, projectApiUrl } from "../../constants";
import { uploadAttachment } from "../../storageUpload";
import { AuthContext } from "../../contexts/AuthContext";
import type { LayoutOutletContext } from "../../components/Layout/Layout";
import { getIdApp } from "../../utils";
import EyeIcon from "../../assets/img/eye.svg";
import EyeSlashIcon from "../../assets/img/eye-slash.svg";
import { useButtonTitles } from "./useButtonTitles";
import "./PersonalData.css";
import {
  buildPersonalDataLoadUrl,
  buildDraftStorageKey,
  clearDraft,
  computeDiff,
  countFilledFields,
  deepMerge,
  defaultCategories,
  extractValues,
  extractFieldMetadata,
  formatDisplayName,
  readDraft,
  replacePersonalDataCategory,
  scopeEmailFieldsPayload,
  scopePersonalDataPayload,
  writeDraft,
} from "./personalData.utils";
import { getInitialLanguagePreference, normalizeLanguageCode, persistLanguagePreference } from "../../languagePreference";
import { getInitialThemePreference, getStoredThemePreference, normalizeThemePreference, persistThemePreference, THEME_PREFERENCE_CHANGE_EVENT } from "../../themePreference";

const getCategoryFromSearchParams = (searchParams: URLSearchParams) => {
  const requestedCategory = searchParams.get("section") || searchParams.get("category");
  return requestedCategory && defaultCategories.includes(requestedCategory)
    ? requestedCategory
    : defaultCategories[0];
};

// docs/security-and-platform-work-plan.md Этап 7 — the access token is an
// HttpOnly cookie now, so appId/email can no longer be decoded from it
// client-side. appId was always just this build's own configured app ID
// (the JWT's own "appId" claim always matched getIdApp() — pd only ever
// authenticates as itself); appObjectId was never actually present in the
// token payload to begin with (idexpress_shared's _create_token never sets
// it), so it was already effectively always "" in production.
const getTokenApplicationIds = () => ({
  appId: getIdApp() || "",
  appObjectId: "",
});

const normalizeEmailClaim = (value: unknown) => {
  if (typeof value !== "string") return "";

  const email = value.trim().toLowerCase();
  return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email) ? email : "";
};

const getAuthenticatedEmail = (fallbackEmail?: string | null) => normalizeEmailClaim(fallbackEmail);

const getApiErrorMessage = (error: unknown) => {
  if (!axios.isAxiosError(error)) return "";

  const payload = error.response?.data || {};
  const errors = payload.errors;
  if (errors && typeof errors === "object") {
    const firstError = ["password", "passwordRepeat", "email", "code"]
      .map((field) => errors[field])
      .find((fieldErrors) => Array.isArray(fieldErrors) && fieldErrors.length > 0);

    if (firstError?.[0]) return String(firstError[0]).trim();
  }

  const details = payload.details;
  const message = payload.message;

  return String(details || message || "").trim();
};

const isPasswordValidForIdExpress = (password: string) => (
  password.length >= 8 &&
  /[A-Z]/.test(password) &&
  /[a-z]/.test(password) &&
  /[0-9]/.test(password) &&
  /[@#$%^&+=]/.test(password)
);

const applyAuthenticatedPrimaryEmail = (data: Record<string, any>, authenticatedEmail: string) =>
  authenticatedEmail && !normalizeEmailClaim(data?.emails?.primary)
    ? deepMerge(data, { emails: { primary: authenticatedEmail } })
    : data;

const removeDraftPrimaryEmailWhenServerHasPrimary = (
  draftData: Record<string, any> | null,
  serverData: Record<string, any>
) => {
  if (!draftData || !normalizeEmailClaim(serverData?.emails?.primary)) return draftData;
  if (!draftData.emails || typeof draftData.emails !== "object") return draftData;

  const nextEmails = { ...draftData.emails };
  delete nextEmails.primary;

  if (Object.keys(nextEmails).length === 0) {
    const { emails, ...nextDraftData } = draftData;
    return Object.keys(nextDraftData).length > 0 ? nextDraftData : null;
  }

  return {
    ...draftData,
    emails: nextEmails,
  };
};

const emailFields = ["primary", "work", "personal", "backup"];
const profileVerificationMaxFileSize = 10 * 1024 * 1024;
const profileVerificationAcceptedTypes = ["image/jpeg", "image/png", "image/webp"];
const profileVerificationProvider = "S.R.L. 'Top100'; IDNO 0000000000000";

const ProfileVerificationIcon: React.FC<{ className?: string }> = ({ className = "" }) => (
  <svg className={className} width="144" height="144" viewBox="0 0 144 144" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <rect width="144" height="144" rx="12" fill="#E8F3FF" />
    <ellipse cx="73" cy="123" rx="43" ry="6" fill="#C8DFF5" />
    <path d="M34 119C34 94 50 78 72 78C94 78 110 94 110 119" fill="#4EA5F5" />
    <path d="M51 91C57 83 64 79 72 79C80 79 87 83 93 91" stroke="#90CAF9" strokeWidth="3" strokeLinecap="round" />
    <rect x="65" y="67" width="14" height="15" rx="5" fill="#F2B28C" />
    <circle cx="72" cy="50" r="24" fill="#F6C7A5" />
    <path d="M48 51C48 34 59 25 73 25C88 25 98 37 96 54C91 48 84 46 77 42C68 37 58 39 48 51Z" fill="#37474F" />
    <circle cx="94" cy="53" r="4" fill="#F2B28C" />
    <circle cx="64" cy="51" r="2" fill="#263238" />
    <circle cx="80" cy="51" r="2" fill="#263238" />
    <path d="M72 54L70 58H74" stroke="#C77B5B" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M65 63C69 66 75 66 79 63" stroke="#A85D45" strokeWidth="2" strokeLinecap="round" />
    <path d="M78 88C87 84 92 76 94 69C95 66 99 67 99 71C97 83 91 92 82 96Z" fill="#F2B28C" />
    <g transform="translate(78 62) rotate(-6)">
      <rect x="2" y="3" width="54" height="34" rx="5" fill="#90A4AE" opacity="0.25" />
      <rect width="54" height="34" rx="5" fill="white" />
      <rect x="1" y="1" width="52" height="32" rx="4" stroke="#D5E2EC" strokeWidth="1.5" />
      <rect x="4" y="4" width="46" height="4" rx="2" fill="#1565C0" />
      <rect x="5" y="11" width="14" height="17" rx="2" fill="#E3F2FD" />
      <circle cx="12" cy="16" r="3.2" fill="#F6C7A5" />
      <path d="M8.5 15C9 12.5 14 12.5 15 15" fill="#37474F" />
      <path d="M8 26C9 22 15 22 16 26" fill="#42A5F5" />
      <rect x="23" y="12" width="20" height="2.5" rx="1.2" fill="#78909C" />
      <rect x="23" y="17" width="16" height="2.5" rx="1.2" fill="#B0BEC5" />
      <rect x="23" y="22" width="23" height="2.5" rx="1.2" fill="#B0BEC5" />
      <rect x="5" y="29" width="35" height="1.8" rx="1" fill="#CFD8DC" />
      <rect x="43" y="25" width="7" height="5" rx="1.5" fill="#FFD54F" />
    </g>
  </svg>
);


type PendingEmailChange = {
  field: string;
  fields: string[];
  email: string;
  code: string;
  confirming: boolean;
};

type EmailChangeRequestResult = Omit<PendingEmailChange, "code"> & {
  code: string | null;
};

const buildEmailMetadata = (field: string, verified: boolean, tags: string[]) => ({
  emails: {
    [field]: {
      __metadata: {
        verified,
        tags,
      },
    },
  },
});

const getEmptyEmailFields = (emails: Record<string, any> = {}) =>
  emailFields.filter((field) => !normalizeEmailClaim(emails?.[field]));

const applyEmptyEmailMetadata = (metadata: Record<string, any>, fields: string[]) =>
  fields.reduce(
    (nextMetadata, field) => deepMerge(nextMetadata, buildEmailMetadata(field, false, [])),
    metadata
  );

const applyAuthenticatedPrimaryEmailMetadata = (
  metadata: Record<string, any>,
  data: Record<string, any>,
  authenticatedEmail: string
) => {
  if (!authenticatedEmail || normalizeEmailClaim(data?.emails?.primary) !== authenticatedEmail) return metadata;

  return deepMerge(metadata, buildEmailMetadata("primary", true, ["auth", "primary"]));
};

const getVerifiedEmailValues = (metadata: Record<string, any>, data: Record<string, any>, authenticatedEmail: string) => {
  const verifiedEmails = new Set<string>();
  const authenticated = normalizeEmailClaim(authenticatedEmail);
  if (authenticated) verifiedEmails.add(authenticated);

  emailFields.forEach((field) => {
    const email = normalizeEmailClaim(data?.emails?.[field]);
    const isVerified = metadata?.emails?.[field]?.__metadata?.verified === true;
    if (email && isVerified) verifiedEmails.add(email);
  });

  return verifiedEmails;
};

const groupEmailVerificationRequests = (requests: Array<{ field: string; email: string }>) => {
  const grouped = new Map<string, { field: string; fields: string[]; email: string }>();

  requests.forEach(({ field, email }) => {
    const normalizedEmail = normalizeEmailClaim(email);
    if (!field || !normalizedEmail) return;

    const current = grouped.get(normalizedEmail) || { field, fields: [], email: normalizedEmail };
    if (!current.fields.includes(field)) current.fields.push(field);
    current.fields.sort((left, right) => emailFields.indexOf(left) - emailFields.indexOf(right));
    current.field = current.fields[0];
    grouped.set(normalizedEmail, current);
  });

  return Array.from(grouped.values());
};

const mergePendingEmailChanges = (changes: PendingEmailChange[]) => {
  const grouped = new Map<string, PendingEmailChange>();

  changes.forEach((change) => {
    const email = normalizeEmailClaim(change.email);
    if (!email) return;

    const current = grouped.get(email);
    const fields = Array.from(new Set([...(current?.fields || []), ...change.fields]))
      .filter((field) => emailFields.includes(field))
      .sort((left, right) => emailFields.indexOf(left) - emailFields.indexOf(right));

    if (fields.length === 0) return;

    grouped.set(email, {
      ...change,
      ...(current || {}),
      email,
      field: fields[0],
      fields,
      code: current?.code || change.code || "",
      confirming: Boolean(current?.confirming || change.confirming),
    });
  });

  return Array.from(grouped.values());
};

const removeEmailChangeFields = (data: Record<string, any>, fields: string[]) => {
  if (!data?.emails) return data;

  const nextEmails = { ...data.emails };
  fields.forEach((field) => delete nextEmails[field]);

  const nextData = { ...data };
  if (Object.keys(nextEmails).length > 0) {
    nextData.emails = nextEmails;
  } else {
    delete nextData.emails;
  }

  return nextData;
};

const getEmailVerificationRequests = (
  data: Record<string, any>,
  originalData: Record<string, any>,
  authenticatedEmail: string,
  metadata: Record<string, any>,
  pendingEmailChanges: PendingEmailChange[]
) => {
  const verifiedEmails = getVerifiedEmailValues(metadata, originalData, authenticatedEmail);
  const requests = emailFields.flatMap((field) => {
    const email = normalizeEmailClaim(data?.emails?.[field]);
    const originalEmail = normalizeEmailClaim(originalData?.emails?.[field]);
    const isChanged = email !== originalEmail;
    const isVerified = metadata?.emails?.[field]?.__metadata?.verified === true;
    const isPending = pendingEmailChanges.some(
      (item) => item.email === email && item.fields.includes(field)
    );

    if (!email || isPending) return [];
    if (verifiedEmails.has(email)) return [];
    if (!isChanged && isVerified) return [];

    return [{ field, email }];
  });

  return groupEmailVerificationRequests(requests);
};

const filterPendingEmailChangesForData = (
  pendingEmailChanges: PendingEmailChange[],
  data: Record<string, any>
) =>
  mergePendingEmailChanges(
    pendingEmailChanges.flatMap((item) => {
      const email = normalizeEmailClaim(item.email);
      const fields = item.fields.filter((field) => normalizeEmailClaim(data?.emails?.[field]) === email);
      if (fields.length === 0) return [];
      return [{ ...item, email, field: fields[0], fields }];
    })
  );

const cleanEmptyOptionalEmailFields = (data: Record<string, any>) => {
  return data;
};

const pickOriginalDataForPayload = (
  originalData: Record<string, any>,
  payload: Record<string, any>
) =>
  Object.keys(payload || {}).reduce<Record<string, any>>((acc, key) => {
    acc[key] = originalData?.[key] ?? {};
    return acc;
  }, {});

const createCroppedAvatarBlob = async (imageSrc: string): Promise<Blob> => {
  const image = new Image();
  image.crossOrigin = "anonymous";
  image.src = imageSrc;

  await new Promise((resolve, reject) => {
    image.onload = () => resolve(null);
    image.onerror = () => reject(new Error("Failed to load avatar image"));
  });

  const canvas = document.createElement("canvas");
  const outputSize = 1024;
  canvas.width = outputSize;
  canvas.height = outputSize;

  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas context unavailable");

  const coverScale = Math.max(outputSize / image.width, outputSize / image.height);
  const drawWidth = image.width * coverScale;
  const drawHeight = image.height * coverScale;
  const overflowX = Math.max(0, drawWidth - outputSize);
  const overflowY = Math.max(0, drawHeight - outputSize);
  const drawX = -(overflowX / 2);
  const drawY = -(overflowY / 2);

  context.clearRect(0, 0, outputSize, outputSize);
  context.drawImage(image, drawX, drawY, drawWidth, drawHeight);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Failed to create cropped avatar blob"));
        return;
      }

      resolve(blob);
    }, "image/jpeg", 0.92);
  });
};

const PersonalData: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { AuthContextState, updateAuthTokens, handleLogOut } = useContext(AuthContext);
  const { theme, setThemePreference, isMobileNavOpen, setIsMobileNavOpen } = useOutletContext<LayoutOutletContext>();
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [originalData, setOriginalData] = useState<Record<string, any>>({});
  const [fieldMetadata, setFieldMetadata] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<string>(() => getCategoryFromSearchParams(searchParams));
  const activeSectionRef = useRef<HTMLElement | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "draft" | "saving" | "saved" | "error">("idle");
  const [savePulse, setSavePulse] = useState(false);
  const [formVersion, setFormVersion] = useState(0);
  const [notice, setNotice] = useState<{ tone: "info" | "success" | "error"; text: string } | null>(null);
  const [pendingEmailChanges, setPendingEmailChanges] = useState<PendingEmailChange[]>([]);
  const [authenticatedEmailNeedsSave, setAuthenticatedEmailNeedsSave] = useState(false);
  const [profileVerificationFile, setProfileVerificationFile] = useState<File | null>(null);
  const [profileVerificationConsent, setProfileVerificationConsent] = useState(false);
  const [profileVerificationSubmitting, setProfileVerificationSubmitting] = useState(false);
  const [profileVerificationExpanded, setProfileVerificationExpanded] = useState(false);
  const [profileVerificationValidationVisible, setProfileVerificationValidationVisible] = useState(false);
  const [accountDeleteRequested, setAccountDeleteRequested] = useState(false);
  const [accountDeleteCode, setAccountDeleteCode] = useState("");
  const [accountDeleteLoading, setAccountDeleteLoading] = useState(false);
  const [accountDeleteEmail, setAccountDeleteEmail] = useState("");
  const [passwordChangeExpanded, setPasswordChangeExpanded] = useState(false);
  const [passwordChangeRequested, setPasswordChangeRequested] = useState(false);
  const [passwordChangeCode, setPasswordChangeCode] = useState("");
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);
  const [passwordChangeEmail, setPasswordChangeEmail] = useState("");
  const [passwordChangeForm, setPasswordChangeForm] = useState({ password: "", passwordRepeat: "" });
  const [passwordFieldVisibility, setPasswordFieldVisibility] = useState({ password: false, passwordRepeat: false });

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const profileVerificationInputRef = useRef<HTMLInputElement | null>(null);
  const submitInFlightRef = useRef(false);

  const userId = AuthContextState.user.userId || AuthContextState.user.sub || AuthContextState.user.id || "";
  const { appId, appObjectId } = getTokenApplicationIds();
  const authenticatedEmail = useMemo(
    () => getAuthenticatedEmail(AuthContextState.user.email),
    [AuthContextState.user.email]
  );
  const storageKey = buildDraftStorageKey(userId, appId, appObjectId || appId);
  const authHeaders = useMemo<Record<string, string>>(() => {
    const headers: Record<string, string> = {};

    if (AuthContextState.accessToken) {
      headers.Authorization = `Bearer ${AuthContextState.accessToken}`;
    }

    return headers;
  }, [AuthContextState.accessToken]);

  const extractAuthTokens = (payload: Record<string, any> | null | undefined) => {
    if (!payload?.accessToken || !payload?.refreshToken || !payload?.accessTokenExpAt || !payload?.refreshTokenExpAt) {
      return null;
    }

    return {
      accessToken: payload.accessToken,
      refreshToken: payload.refreshToken,
      accessTokenExpAt: payload.accessTokenExpAt,
      refreshTokenExpAt: payload.refreshTokenExpAt,
    };
  };

  const updateTokensAfterPrimaryEmailChange = async (payload: Record<string, any> | null | undefined) => {
    if (!payload?.tokensInvalidated) return;

    const nextTokens = extractAuthTokens(payload);
    if (!nextTokens) {
      throw new Error("Primary email changed but token response is missing.");
    }

    await updateAuthTokens(nextTokens);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) {
        setLoading(false);
        setNotice({ tone: "error", text: t("personalData.messages.loadError", { defaultValue: "Unable to identify the current user." }) });
        return;
      }

      if (!appId) {
        setLoading(false);
        setNotice({
          tone: "error",
          text: t("personalData.messages.loadError", { defaultValue: "Unable to identify the current application." }),
        });
        return;
      }

      if (!projectApiUrl) {
        setLoading(false);
        setNotice({
          tone: "error",
          text: t("personalData.messages.loadError", { defaultValue: "Personal data API URL is not configured." }),
        });
        return;
      }

      try {
        const loadUrl = buildPersonalDataLoadUrl({
          baseUrl: projectApiUrl,
          userId,
          appId,
          appObjectId: appObjectId || undefined,
        });

        const [res, pendingEmailResponse, pendingAccountDeleteResponse, pendingPasswordChangeResponse] = await Promise.all([
          axios.get(loadUrl, {
            headers: authHeaders,
            withCredentials: true,
          }),
          axios.get(`${projectApiUrl}/personal_data/email_change`, {
            headers: authHeaders,
            withCredentials: true,
          }),
          axios.get(`${projectApiUrl}/personal_data/account_delete`, {
            headers: authHeaders,
            withCredentials: true,
          }),
          axios.get(`${projectApiUrl}/personal_data/password_change`, {
            headers: authHeaders,
            withCredentials: true,
          }),
        ]);
        const rawServerData = res.data?.data?.[0]?.data || {};
        const serverData = rawServerData ? extractValues(rawServerData) : {};
        const serverMetadata = rawServerData ? extractFieldMetadata(rawServerData) : {};
        const rawDraftData = readDraft(storageKey);
        const draftData = removeDraftPrimaryEmailWhenServerHasPrimary(rawDraftData, serverData);
        if (rawDraftData && rawDraftData !== draftData) {
          if (draftData) {
            writeDraft(storageKey, draftData);
          } else {
            clearDraft(storageKey);
          }
        }
        const hydratedData = draftData ? deepMerge(serverData, draftData) : serverData;
        const savedLanguage = normalizeLanguageCode(hydratedData?.preferences?.language);
        const preferredLanguage = savedLanguage || getInitialLanguagePreference();
        const savedThemePreference = normalizeThemePreference(hydratedData?.preferences?.themeMode);
        const storedThemePreference = getStoredThemePreference();
        const preferredThemePreference = savedThemePreference || storedThemePreference || getInitialThemePreference();
        const hydratedWithLanguage =
          preferredLanguage && !savedLanguage
            ? deepMerge(hydratedData, { preferences: { language: preferredLanguage } })
            : hydratedData;
        const hydratedWithPreferences =
          preferredThemePreference && preferredThemePreference !== savedThemePreference
            ? deepMerge(hydratedWithLanguage, { preferences: { themeMode: preferredThemePreference } })
            : hydratedWithLanguage;
        const serverPrimaryEmail = normalizeEmailClaim(serverData?.emails?.primary);
        const nextHydratedData = applyAuthenticatedPrimaryEmail(hydratedWithPreferences, authenticatedEmail);

        if (preferredLanguage) {
          persistLanguagePreference(preferredLanguage);
          if (preferredLanguage !== i18n.language) {
            i18n.changeLanguage(preferredLanguage);
          }
        }

        if (preferredThemePreference) {
          persistThemePreference(preferredThemePreference);
          setThemePreference(preferredThemePreference);
        }

        if (storedThemePreference && !savedThemePreference) {
          const config = generateRequestConfig(
            "put",
            "personal_data",
            { data: { preferences: { themeMode: storedThemePreference } } },
            authHeaders
          );
          axios(config).catch((syncError) => {
            console.warn("Failed to sync stored theme preference", syncError);
          });
        }

        setDraftRestored(Boolean(draftData));
        setFormData(nextHydratedData);
        setOriginalData(draftData ? serverData : nextHydratedData);
        setFieldMetadata(applyAuthenticatedPrimaryEmailMetadata(serverMetadata, nextHydratedData, authenticatedEmail));
        setPendingEmailChanges(
          filterPendingEmailChangesForData(
            groupEmailVerificationRequests(
              (pendingEmailResponse.data?.data || []).flatMap((item: any) => {
                const fields = Array.isArray(item.fields) && item.fields.length > 0 ? item.fields : [item.field];
                return fields.map((field: string) => ({ field, email: item.email }));
              })
            ).map((item) => ({
              ...item,
              code: "",
              confirming: false,
            })),
            nextHydratedData
          )
        );
        const pendingAccountDelete = pendingAccountDeleteResponse.data?.data || null;
        const pendingPasswordChange = pendingPasswordChangeResponse.data?.data || null;
        setAccountDeleteRequested(Boolean(pendingAccountDelete));
        setAccountDeleteEmail(pendingAccountDelete?.email || "");
        setPasswordChangeRequested(Boolean(pendingPasswordChange));
        setPasswordChangeEmail(pendingPasswordChange?.email || "");
        setPasswordChangeExpanded(Boolean(pendingPasswordChange));
        setAuthenticatedEmailNeedsSave(Boolean(authenticatedEmail && !serverPrimaryEmail));
        setAvatarPreview(nextHydratedData?.profile?.avatar || null);
        setFormVersion((current) => current + 1);
      } catch (err) {
        console.error(t("personalData.errors.userDataLoad"), err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [appId, appObjectId, authHeaders, authenticatedEmail, i18n, setThemePreference, storageKey, t, userId]);

  useEffect(() => {
    setActiveCategory(getCategoryFromSearchParams(searchParams));
  }, [searchParams]);

  useEffect(() => {
    if (loading) return;

    const draftData = computeDiff(formData, originalData);
    const draftExists = Object.keys(draftData).length > 0;
    if (draftExists) {
      writeDraft(storageKey, draftData);
    } else {
      clearDraft(storageKey);
      setDraftRestored(false);
    }

    setSaveState((current) => {
      if (current === "saving" || current === "error" || current === "saved") return current;
      return draftExists ? "draft" : "idle";
    });
  }, [formData, loading, originalData, storageKey]);

  useEffect(() => {
    const handleThemePreferenceChange = (event: Event) => {
      const selectedThemePreference = normalizeThemePreference((event as CustomEvent).detail?.themeMode);
      if (!selectedThemePreference) return;

      persistThemePreference(selectedThemePreference);
      setThemePreference(selectedThemePreference);
      setFormData((current) => deepMerge(current, { preferences: { themeMode: selectedThemePreference } }));
      setOriginalData((current) => deepMerge(current, { preferences: { themeMode: selectedThemePreference } }));

      if (loading || !userId || !appId) return;

      setSaveState("saving");
      const config = generateRequestConfig(
        "put",
        "personal_data",
        { data: { preferences: { themeMode: selectedThemePreference } } },
        authHeaders
      );

      axios(config)
        .then(() => {
          setSaveState("saved");
          setSavePulse(true);
          setNotice({ tone: "success", text: t("personalData.messages.dataSaved") });
        })
        .catch((error) => {
          console.error(t("personalData.errors.saveFailed"), error);
          setSaveState("error");
          setNotice({ tone: "error", text: t("personalData.messages.saveError") });
        });
    };

    window.addEventListener(THEME_PREFERENCE_CHANGE_EVENT, handleThemePreferenceChange);
    return () => window.removeEventListener(THEME_PREFERENCE_CHANGE_EVENT, handleThemePreferenceChange);
  }, [appId, appObjectId, authHeaders, loading, setThemePreference, t, userId]);

  useEffect(() => {
    if (!savePulse) return;

    const timeoutId = window.setTimeout(() => setSavePulse(false), 1800);
    return () => window.clearTimeout(timeoutId);
  }, [savePulse]);

  useEffect(() => {
    if (saveState !== "saved") return;
    const timeoutId = window.setTimeout(() => setSaveState("idle"), 1800);
    return () => window.clearTimeout(timeoutId);
  }, [saveState]);

  useEffect(() => {
    if (!notice) return;
    const timeoutId = window.setTimeout(() => setNotice(null), 3200);
    return () => window.clearTimeout(timeoutId);
  }, [notice]);

  useEffect(() => {
    if (!isMobileNavOpen) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileNavOpen]);

  const topCategories = Array.from(new Set([
    ...defaultCategories,
    ...Object.keys(formData).filter((category) => category !== "residence" && category !== "taxRecords"),
  ]));
  const activeCategoryData = formData[activeCategory] ?? {};
  const categoryIsEmpty = countFilledFields(activeCategoryData) === 0;
  const hasUnsavedChanges = Object.keys(computeDiff(formData, originalData)).length > 0;
  const profile = formData.profile || {};
  const accountVerification = profile.accountVerification || {};
  const accountVerificationStatus = accountVerification.status || "not_submitted";
  const accountIsVerified = accountVerificationStatus === "verified";
  const accountVerificationHasRequest = accountVerificationStatus === "pending";
  const accountVerificationFileName =
    profileVerificationFile?.name || accountVerification.selfieDocumentPhoto?.fileName || "";
  const profileVerificationHasPhoto = Boolean(profileVerificationFile || accountVerification.selfieDocumentPhoto?.fileName);
  const profileVerificationMissingFields = [
    !profileVerificationHasPhoto
      ? t("personalData.accountVerification.missingPhoto", { defaultValue: "Selfie with passport" })
      : "",
    !profileVerificationConsent
      ? t("personalData.accountVerification.missingConsent", { defaultValue: "Consent to provide personal data" })
      : "",
  ].filter(Boolean);
  const profileVerificationCanSubmit = profileVerificationMissingFields.length === 0;
  const verifyPath = `/${(i18n.language || "ru").split("-")[0] || "ru"}/verify`;
  const showProfileVerificationForm = !accountIsVerified && profileVerificationExpanded;

  const openFilePicker = () => fileInputRef.current?.click();
  const openProfileVerificationFilePicker = () => profileVerificationInputRef.current?.click();

  const processAvatarFile = (file?: File) => {
    if (!file) return;

    setAvatarFile(file);
    const reader = new FileReader();
    reader.onload = () => setAvatarPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const processProfileVerificationFile = (file?: File) => {
    if (!file) return;

    if (!profileVerificationAcceptedTypes.includes(file.type)) {
      setNotice({
        tone: "error",
        text: t("personalData.accountVerification.invalidFileType", {
          defaultValue: "Upload a JPG, PNG or WebP photo.",
        }),
      });
      return;
    }

    if (file.size > profileVerificationMaxFileSize) {
      setNotice({
        tone: "error",
        text: t("personalData.accountVerification.fileTooLarge", {
          defaultValue: "Photo size must not exceed 10 MB.",
        }),
      });
      return;
    }

    setProfileVerificationFile(file);
    setProfileVerificationValidationVisible(false);
  };

  const handleShellClick = (event: React.MouseEvent<HTMLElement>) => {
    const actionElement = (event.target as HTMLElement).closest<HTMLElement>("[data-action]");
    if (!actionElement) return;

    const { action, category } = actionElement.dataset;

    if (action === "select-category" && category) {
      setActiveCategory(category);
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set("section", category);
      navigate({ search: nextSearchParams.toString() }, { replace: true });
      setIsMobileNavOpen(false);
      window.requestAnimationFrame(() => {
        activeSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        activeSectionRef.current?.focus({ preventScroll: true });
      });
    }

    if (action === "open-file-picker") {
      openFilePicker();
    }

    if (action === "open-profile-verification-file-picker") {
      openProfileVerificationFilePicker();
    }

    if (action === "toggle-profile-verification") {
      setProfileVerificationExpanded((current) => !current);
    }

    if (action === "open-profile-verification-section") {
      setActiveCategory("profile");
      setProfileVerificationExpanded(true);
      const nextSearchParams = new URLSearchParams(searchParams);
      nextSearchParams.set("section", "profile");
      navigate({ search: nextSearchParams.toString() }, { replace: true });
      window.setTimeout(() => {
        document.getElementById("pd-profile-verification")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }

    if (action === "clear-draft") {
      clearDraft(storageKey);
      setFormData(originalData);
      setAvatarFile(null);
      setAvatarPreview(originalData?.profile?.avatar || null);
      setDraftRestored(false);
      setSaveState("idle");
      setFormVersion((current) => current + 1);
    }

    if (action === "confirm-email-change") {
      const emailField = actionElement.dataset.emailField;
      if (emailField) confirmEmailChange(emailField);
    }
  };

  const handleFormDataChange = (category: string, categoryData: Record<string, any>) => {
    if (category === "preferences") {
      const selectedLanguage = normalizeLanguageCode(categoryData?.language);
      if (selectedLanguage) {
        persistLanguagePreference(selectedLanguage);
        if (selectedLanguage !== i18n.language) {
          i18n.changeLanguage(selectedLanguage);
        }
      }

      const selectedThemePreference = normalizeThemePreference(categoryData?.themeMode);
      if (selectedThemePreference) {
        persistThemePreference(selectedThemePreference);
        setThemePreference(selectedThemePreference);
      }
    }

    if (category === "emails") {
      setPendingEmailChanges((current) =>
        filterPendingEmailChangesForData(current, { emails: categoryData })
      );

      getEmptyEmailFields(categoryData).forEach((field) => {
        setFieldMetadata((prev) =>
          deepMerge(prev, buildEmailMetadata(field, false, []))
        );
      });

      const requestedPrimaryEmail = normalizeEmailClaim(categoryData?.primary);
      if (requestedPrimaryEmail && authenticatedEmail) {
        setFieldMetadata((prev) =>
          deepMerge(
            prev,
            requestedPrimaryEmail === authenticatedEmail
              ? buildEmailMetadata("primary", true, ["auth", "primary"])
              : buildEmailMetadata("primary", false, ["email-change-required"])
          )
        );
      }

      emailFields
        .filter((field) => field !== "primary")
        .forEach((field) => {
          const requestedEmail = normalizeEmailClaim(categoryData?.[field]);
          const originalEmail = normalizeEmailClaim(originalData?.emails?.[field]);
          if (!requestedEmail || requestedEmail === originalEmail) return;

          setFieldMetadata((prev) =>
            deepMerge(prev, buildEmailMetadata(field, false, ["email-change-required"]))
          );
        });
    }

    setSaveState((current) => (current === "saving" ? current : "draft"));
    setFormData((prev) => ({
      ...prev,
      [category]: categoryData,
    }));
  };

  const handleSubmit = async (data: Record<string, any>, category: string) => {
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;

    const submittedData = scopePersonalDataPayload(data, category);
    if (Object.keys(submittedData).length === 0) {
      submitInFlightRef.current = false;
      return;
    }

    const isEmailForm = category === "emails";
    const requestedPrimaryEmail = normalizeEmailClaim(submittedData?.emails?.primary);
    const hasPrimaryEmailChange = Boolean(
      isEmailForm &&
      authenticatedEmail &&
      requestedPrimaryEmail &&
      requestedPrimaryEmail !== authenticatedEmail
    );
    const emailVerificationRequests = isEmailForm
      ? getEmailVerificationRequests(
          submittedData,
          originalData,
          authenticatedEmail,
          fieldMetadata,
          pendingEmailChanges
        )
      : [];
    const changedEmailFields = emailVerificationRequests.flatMap((item) => item.fields);
    const normalizedData = isEmailForm && !hasPrimaryEmailChange
      ? applyAuthenticatedPrimaryEmail(submittedData, authenticatedEmail)
      : submittedData;
    const comparisonOriginalData = pickOriginalDataForPayload(originalData, normalizedData);
    let diffData = computeDiff(normalizedData, comparisonOriginalData);
    if (hasPrimaryEmailChange && requestedPrimaryEmail) {
      diffData = deepMerge(diffData, {
        emails: {
          primary: requestedPrimaryEmail,
        },
      });
    } else if (isEmailForm && authenticatedEmailNeedsSave && authenticatedEmail) {
      diffData = deepMerge(diffData, {
        emails: {
          primary: authenticatedEmail,
        },
      });
    }
    if (Object.keys(diffData).length === 0 && emailVerificationRequests.length === 0) {
      setSaveState("idle");
      setNotice({ tone: "info", text: t("personalData.messages.noChanges") });
      submitInFlightRef.current = false;
      return;
    }

    try {
      setSaveState("saving");
      let requestedChanges: EmailChangeRequestResult[] = [];

      let authTokenPayload: Record<string, any> | null = null;

      if (emailVerificationRequests.length > 0) {
        requestedChanges = await Promise.all(
          emailVerificationRequests.map(async ({ field, fields, email }) => {
            const response = await axios.post(
              `${projectApiUrl}/personal_data/email_change`,
              {
                field,
                fields,
                email,
                emails: scopeEmailFieldsPayload(submittedData?.emails || {}, fields),
              },
              {
                headers: authHeaders,
                withCredentials: true,
              }
            );
            const responseFields = Array.isArray(response.data?.fields) && response.data.fields.length > 0
              ? response.data.fields
              : fields;
            if (response.data?.tokensInvalidated) {
              authTokenPayload = response.data;
            }

            return {
              field: responseFields[0],
              fields: responseFields,
              email,
              code: response.status === 202 ? "" : null,
              confirming: false,
            };
          })
        );

        const immediatelyVerifiedChanges = requestedChanges.filter((requested) => requested.code === null);
        setPendingEmailChanges((current) => {
          const pendingRequestedChanges = requestedChanges
            .filter((requested) => requested.code !== null)
            .map((requested) => ({ ...requested, code: requested.code || "" }));
          const next = current.filter(
            (item) => !requestedChanges.some((requested) => (
              requested.email === item.email || requested.fields.some((field) => item.fields.includes(field))
            ))
          );
          return mergePendingEmailChanges([...next, ...pendingRequestedChanges]);
        });
        if (immediatelyVerifiedChanges.length > 0) {
          setFieldMetadata((prev) =>
            immediatelyVerifiedChanges.reduce(
              (metadata, item) => item.fields.reduce(
                (nextMetadata, field) => deepMerge(nextMetadata, buildEmailMetadata(field, true, ["verified", field])),
                metadata
              ),
              prev
            )
          );
        }
        diffData = removeEmailChangeFields(diffData, changedEmailFields);
      }
      diffData = cleanEmptyOptionalEmailFields(diffData);

      if (emailVerificationRequests.length === 0 && Object.keys(diffData).length === 0) {
        setSaveState("idle");
        setNotice({ tone: "info", text: t("personalData.messages.noChanges") });
        submitInFlightRef.current = false;
        return;
      }

      let noChangesResponse = false;
      if (Object.keys(diffData).length > 0) {
        const config = generateRequestConfig(
          "put",
          "personal_data",
          { data: diffData },
          authHeaders
        );

        const response = await axios(config);
        noChangesResponse = response.data?.updated_count === 0 && emailVerificationRequests.length === 0;
        if (response.data?.tokensInvalidated) {
          authTokenPayload = response.data;
        }
      }
      const savedLanguage = normalizeLanguageCode(diffData?.preferences?.language);
      if (savedLanguage) {
        persistLanguagePreference(savedLanguage);
        if (savedLanguage !== i18n.language) {
          await i18n.changeLanguage(savedLanguage);
        }

        if (typeof window !== "undefined") {
          const currentUrl = new URL(window.location.href);
          const nextPath = currentUrl.pathname.replace(/^\/([A-Za-z]{2})\//, (match, capturedLanguage) =>
            match.replace(capturedLanguage, savedLanguage)
          );
          navigate(`${nextPath}${currentUrl.search}`, { replace: true });
        }
      }

      const savedThemePreference = normalizeThemePreference(diffData?.preferences?.themeMode);
      if (savedThemePreference) {
        persistThemePreference(savedThemePreference);
        setThemePreference(savedThemePreference);
      }

      await updateTokensAfterPrimaryEmailChange(authTokenPayload);

      const hasPendingEmailConfirmation = requestedChanges.some((requested) => requested.code !== null);
      setDraftRestored(false);
      setOriginalData((current) =>
        replacePersonalDataCategory(current, category, normalizedData[category])
      );
      setFormData((current) =>
        replacePersonalDataCategory(current, category, normalizedData[category])
      );
      setFormVersion((current) => current + 1);

      if (isEmailForm) {
        const verifiedEmailValues = getVerifiedEmailValues(fieldMetadata, originalData, authenticatedEmail);
        setFieldMetadata((current) =>
          emailFields.reduce((metadata, emailField) => {
            const emailValue = normalizeEmailClaim(normalizedData?.emails?.[emailField]);
            if (!emailValue || !verifiedEmailValues.has(emailValue)) return metadata;
            return deepMerge(metadata, buildEmailMetadata(emailField, true, ["verified", emailField]));
          }, current)
        );

        if (normalizeEmailClaim(normalizedData?.emails?.primary) === authenticatedEmail) {
          setAuthenticatedEmailNeedsSave(false);
        }
      }
      setSaveState("saved");
      setSavePulse(true);
      setNotice(
        hasPendingEmailConfirmation
          ? null
          : {
              tone: noChangesResponse ? "info" : "success",
              text: noChangesResponse
                ? t("personalData.messages.noChanges")
                : t("personalData.messages.dataSaved"),
            }
      );
    } catch (err) {
      console.error(t("personalData.errors.saveFailed"), err);
      setSaveState("error");
      setNotice({ tone: "error", text: t("personalData.messages.saveError") });
      throw err;
    } finally {
      submitInFlightRef.current = false;
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile || !avatarPreview) return;

    try {
      setAvatarUploading(true);
      const croppedAvatar = await createCroppedAvatarBlob(avatarPreview);
      const croppedFile = new File(
        [croppedAvatar],
        `${avatarFile.name.replace(/\.[^.]+$/, "") || "avatar"}.jpg`,
        { type: croppedAvatar.type || "image/jpeg" }
      );

      const attachment = await uploadAttachment(croppedFile, "avatar", "profile.avatar");

      const newAvatarUrl = attachment.url || avatarPreview;
      setAvatarPreview(newAvatarUrl);
      setAvatarFile(null);
      setFormData((prev) => ({
        ...prev,
        profile: { ...prev.profile, avatar: attachment },
      }));
      setOriginalData((prev) => ({
        ...prev,
        profile: { ...prev.profile, avatar: attachment },
      }));
      setSavePulse(true);
      setNotice({ tone: "success", text: t("personalData.messages.avatarUpdated") });
    } catch (err) {
      console.error(t("personalData.errors.avatarUploadFailed"), err);
      setNotice({ tone: "error", text: t("personalData.messages.avatarUploadError") });
    } finally {
      setAvatarUploading(false);
    }
  };

  const submitProfileVerification = async () => {
    if (accountIsVerified || profileVerificationSubmitting) return;

    if (!profileVerificationCanSubmit) {
      setProfileVerificationValidationVisible(true);
      setNotice({
        tone: "error",
        text: t("personalData.accountVerification.validationSummary", {
          defaultValue: "Complete the required fields before sending the request.",
        }),
      });
      return;
    }

    try {
      setProfileVerificationSubmitting(true);

      const uploadedSelfie = profileVerificationFile
        ? await uploadAttachment(
            profileVerificationFile,
            "kyc_document",
            "profile.accountVerification.selfieDocumentPhoto"
          )
        : null;

      const now = new Date().toISOString();
      const nextVerification = {
        ...accountVerification,
        status: "pending",
        selfieDocumentPhoto: uploadedSelfie
          ? {
              fileId: uploadedSelfie.fileId,
              fileName: uploadedSelfie.fileName,
              mimeType: uploadedSelfie.mimeType,
              size: uploadedSelfie.sizeBytes,
              url: uploadedSelfie.url,
            }
          : accountVerification.selfieDocumentPhoto,
        consentAccepted: true,
        consentAcceptedAt: accountVerification.consentAcceptedAt || now,
        submittedAt: now,
        provider: profileVerificationProvider,
        metadata: {
          ...(accountVerification.metadata || {}),
          verified: false,
          tags: Array.from(new Set([...(accountVerification.metadata?.tags || []), "account-verification", "pending"])),
        },
      };
      const nextData = deepMerge(formData, {
        profile: {
          accountVerification: nextVerification,
        },
      });

      setFormData(nextData);
      await handleSubmit(nextData, "profile");
      setProfileVerificationFile(null);
      setNotice({
        tone: "success",
        text: t("personalData.accountVerification.requestSent", {
          defaultValue: "Profile verification request has been sent.",
        }),
      });
    } catch (err) {
      console.error("Profile verification submission failed", err);
      setNotice({
        tone: "error",
        text: t("personalData.accountVerification.submitFailed", {
          defaultValue: "Could not send the verification request. Please try again.",
        }),
      });
    } finally {
      setProfileVerificationSubmitting(false);
    }
  };

  const updatePasswordChangeForm = (field: "password" | "passwordRepeat", value: string) => {
    setPasswordChangeForm((current) => ({ ...current, [field]: value }));
  };

  const togglePasswordFieldVisibility = (field: "password" | "passwordRepeat") => {
    setPasswordFieldVisibility((current) => ({ ...current, [field]: !current[field] }));
  };

  const getPasswordChangeValidationMessage = () => {
    if (!isPasswordValidForIdExpress(passwordChangeForm.password)) {
      return t("personalData.security.passwordRequirements", {
        defaultValue: "Password must be at least 8 characters long, contain uppercase and lowercase letters, include at least one number and one special character: @ # $ % ^ & + =.",
      });
    }

    if (!isPasswordValidForIdExpress(passwordChangeForm.passwordRepeat)) {
      return t("personalData.security.passwordRepeatRequirements", {
        defaultValue: "Repeat the password using the same requirements.",
      });
    }

    if (passwordChangeForm.password !== passwordChangeForm.passwordRepeat) {
      return t("personalData.security.passwordsDoNotMatch", {
        defaultValue: "New password and confirm password do not match.",
      });
    }

    return "";
  };

  const requestPasswordChange = async () => {
    if (passwordChangeLoading) return;

    const validationMessage = getPasswordChangeValidationMessage();
    if (validationMessage) {
      setNotice({ tone: "error", text: validationMessage });
      return;
    }

    try {
      setPasswordChangeLoading(true);
      const response = await axios.post(
        `${projectApiUrl}/personal_data/password_change`,
        passwordChangeForm,
        {
          headers: authHeaders,
          withCredentials: true,
        }
      );
      setPasswordChangeRequested(true);
      setPasswordChangeExpanded(true);
      setPasswordChangeEmail(response.data?.email || authenticatedEmail || "");
      setPasswordChangeCode("");
      setNotice({
        tone: "success",
        text: t("personalData.security.passwordCodeSent", {
          defaultValue: "Confirmation code sent to your email.",
        }),
      });
    } catch (error) {
      console.error(t("personalData.errors.saveFailed"), error);
      setNotice({
        tone: "error",
        text: getApiErrorMessage(error) || t("personalData.security.passwordRequestError", {
          defaultValue: "Unable to send password change confirmation code.",
        }),
      });
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const confirmPasswordChange = async () => {
    if (passwordChangeLoading || passwordChangeCode.trim().length < 6) return;

    const validationMessage = getPasswordChangeValidationMessage();
    if (validationMessage) {
      setNotice({ tone: "error", text: validationMessage });
      return;
    }

    try {
      setPasswordChangeLoading(true);
      const response = await axios.put(
        `${projectApiUrl}/personal_data/password_change`,
        { ...passwordChangeForm, code: passwordChangeCode.trim() },
        {
          headers: authHeaders,
          withCredentials: true,
        }
      );
      await updateTokensAfterPrimaryEmailChange(response.data);
      setPasswordChangeRequested(false);
      setPasswordChangeCode("");
      setPasswordChangeEmail("");
      setPasswordChangeExpanded(false);
      setPasswordChangeForm({ password: "", passwordRepeat: "" });
      setPasswordFieldVisibility({ password: false, passwordRepeat: false });
      setNotice({
        tone: "success",
        text: t("personalData.security.passwordChanged", {
          defaultValue: "Password changed successfully.",
        }),
      });
    } catch (error) {
      console.error(t("personalData.errors.saveFailed"), error);
      setNotice({
        tone: "error",
        text: getApiErrorMessage(error) || t("personalData.security.passwordConfirmError", {
          defaultValue: "Unable to confirm password change. Check the code and try again.",
        }),
      });
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  const requestAccountDelete = async () => {
    if (accountDeleteLoading) return;

    try {
      setAccountDeleteLoading(true);
      const response = await axios.post(
        `${projectApiUrl}/personal_data/account_delete`,
        {},
        {
          headers: authHeaders,
          withCredentials: true,
        }
      );
      setAccountDeleteRequested(true);
      setAccountDeleteEmail(response.data?.email || authenticatedEmail || "");
      setAccountDeleteCode("");
      setNotice({
        tone: "success",
        text: t("personalData.accountDelete.codeSent", {
          defaultValue: "Confirmation code sent to your email.",
        }),
      });
    } catch (error) {
      console.error(t("personalData.errors.saveFailed"), error);
      setNotice({
        tone: "error",
        text: getApiErrorMessage(error) || t("personalData.accountDelete.requestError", {
          defaultValue: "Unable to send account deletion confirmation code.",
        }),
      });
    } finally {
      setAccountDeleteLoading(false);
    }
  };

  const confirmAccountDelete = async () => {
    if (accountDeleteLoading || accountDeleteCode.trim().length < 6) return;

    try {
      setAccountDeleteLoading(true);
      const response = await axios.put(
        `${projectApiUrl}/personal_data/account_delete`,
        { code: accountDeleteCode.trim() },
        {
          headers: authHeaders,
          withCredentials: true,
        }
      );
      clearDraft(storageKey);
      setNotice({
        tone: "success",
        text: t("personalData.accountDelete.confirmed", {
          defaultValue: "Account deletion confirmed. Your session has been closed.",
        }),
      });
      await handleLogOut({ logout: response.data?.logout, urlCallback: "/" });
      window.setTimeout(() => {
        navigate("/");
      }, 1600);
    } catch (error) {
      console.error(t("personalData.errors.saveFailed"), error);
      setNotice({
        tone: "error",
        text: getApiErrorMessage(error) || t("personalData.accountDelete.confirmError", {
          defaultValue: "Unable to confirm account deletion. Check the code and try again.",
        }),
      });
    } finally {
      setAccountDeleteLoading(false);
    }
  };

  const confirmEmailChange = async (field: string) => {
    const pendingEmailChange = pendingEmailChanges.find((item) => item.field === field);
    if (!pendingEmailChange?.email || pendingEmailChange.code.trim().length < 6) return;

    try {
      setPendingEmailChanges((current) =>
        current.map((item) => item.field === field ? { ...item, confirming: true } : item)
      );
      const response = await axios.put(
        `${projectApiUrl}/personal_data/email_change`,
        {
          field: pendingEmailChange.field,
          email: pendingEmailChange.email,
          fields: pendingEmailChange.fields,
          code: pendingEmailChange.code.trim(),
        },
        {
          headers: authHeaders,
          withCredentials: true,
        }
      );

      await updateTokensAfterPrimaryEmailChange(response.data);
      const confirmedEmails = pendingEmailChange.fields.reduce<Record<string, string>>(
        (emails, emailField) => ({ ...emails, [emailField]: pendingEmailChange.email }),
        {}
      );
      setOriginalData((current) => deepMerge(current, { emails: confirmedEmails }));
      setFormData((current) => deepMerge(current, { emails: confirmedEmails }));
      setFieldMetadata((current) =>
        pendingEmailChange.fields.reduce(
          (metadata, emailField) =>
            deepMerge(metadata, buildEmailMetadata(emailField, true, ["verified", emailField])),
          current
        )
      );
      setPendingEmailChanges((current) =>
        current.filter((item) => item.email !== pendingEmailChange.email)
      );
      setFormVersion((current) => current + 1);
      setNotice({
        tone: "success",
        text: t("personalData.messages.emailChangeConfirmed", {
          defaultValue: "Email confirmed. Your session has been refreshed.",
        }),
      });
    } catch (error) {
      console.error(t("personalData.errors.saveFailed"), error);
      setPendingEmailChanges((current) =>
        current.map((item) => item.field === field ? { ...item, confirming: false } : item)
      );
      const apiMessage = getApiErrorMessage(error);
      setNotice({
        tone: "error",
        text:
          apiMessage ||
          t("personalData.messages.emailChangeConfirmError", {
            defaultValue: "Email confirmation failed. Check the code and try again.",
          }),
      });
    }
  };

  const displayName = formatDisplayName(
    profile,
    t("personalData.ui.idProfile", { defaultValue: "ID profile" })
  );
  const statusLabel =
    saveState === "saving"
      ? t("personalData.ui.statusSaving", { defaultValue: "Saving live changes..." })
      : saveState === "saved"
        ? t("personalData.ui.statusSaved", { defaultValue: "All changes synced" })
        : saveState === "error"
          ? t("personalData.ui.statusError", { defaultValue: "Sync needs attention" })
          : hasUnsavedChanges
            ? t("personalData.ui.statusDraft", { defaultValue: "Changes saved to draft" })
            : t("personalData.ui.statusStable", { defaultValue: "Workspace is up to date" });
  const buttonTitlesRef = useButtonTitles(
    t("personalData.security.openAction", { defaultValue: "Open" })
  );

  const securityActions = [
    {
      key: "changePassword",
      marker: "PW",
      title: t("personalData.security.changePassword", { defaultValue: "Change password" }),
      description: t("personalData.security.changePasswordDescription", {
        defaultValue: "Update the password used to sign in to ID.Express projects.",
      }),
    },
    {
      key: "twoFactorAuthentication",
      marker: "2F",
      title: t("personalData.security.twoFactorAuthentication", { defaultValue: "Two-factor authentication" }),
      description: t("personalData.security.twoFactorAuthenticationDescription", {
        defaultValue: "Add an extra verification step for account sign-in.",
      }),
    },
    {
      key: "activeDevices",
      marker: "DV",
      title: t("personalData.security.activeDevices", { defaultValue: "Active devices" }),
      description: t("personalData.security.activeDevicesDescription", {
        defaultValue: "Review devices and sessions that currently have access to your account.",
      }),
    },
    {
      key: "activityLog",
      marker: "LG",
      title: t("personalData.security.activityLog", { defaultValue: "Activity log" }),
      description: t("personalData.security.activityLogDescription", {
        defaultValue: "Check recent account security events and authorization actions.",
      }),
    },
    {
      key: "applicationAccess",
      marker: "AP",
      title: t("personalData.applicationAccess.title", { defaultValue: "Application access" }),
      description: t("personalData.applicationAccess.description", {
        defaultValue: "Review which applications can access individual fields of your personal data.",
      }),
    },
  ];

  const renderSecuritySection = () => (
    <div className="pd-security-panel">
      <div className="pd-security-panel__intro">
        <p>
          {t("personalData.security.description", {
            defaultValue: "Manage account access, sign-in protection, active sessions and recent security events.",
          })}
        </p>
      </div>

      <div className="pd-security-grid">
        {securityActions.map((action) => {
          const isPasswordAction = action.key === "changePassword";
          const isActiveDevicesAction = action.key === "activeDevices";
          const isActivityLogAction = action.key === "activityLog";
          const isApplicationAccessAction = action.key === "applicationAccess";
          const actionEnabled = isPasswordAction || isActiveDevicesAction || isActivityLogAction || isApplicationAccessAction;
          const actionButtonLabel = isPasswordAction && passwordChangeExpanded
            ? t("personalData.security.hideAction", { defaultValue: "Hide" })
            : t("personalData.security.openAction", { defaultValue: "Open" });

          return (
            <article className={`pd-security-card ${isPasswordAction && passwordChangeExpanded ? "is-expanded" : ""}`} key={action.key}>
              <span className="pd-security-card__icon" aria-hidden="true">{action.marker}</span>
              <div className="pd-security-card__body">
                <strong>{action.title}</strong>
                <p>{action.description}</p>
              </div>
              <button
                type="button"
                className="pd-secondary-button pd-security-card__button"
                title={`${actionButtonLabel}: ${action.title}`}
                onClick={() => {
                  if (isPasswordAction) {
                    setPasswordChangeExpanded((current) => !current);
                    return;
                  }
                  if (isActiveDevicesAction) {
                    navigate(`/${i18n.language}/personal_data/active_devices`);
                    return;
                  }
                  if (isActivityLogAction) {
                    navigate(`/${i18n.language}/personal_data/activity_logs`);
                    return;
                  }
                  if (isApplicationAccessAction) {
                    navigate(`/${i18n.language}/personal_data/application_access`);
                  }
                }}
                disabled={!actionEnabled}
              >
                {actionButtonLabel}
              </button>

              {isPasswordAction && passwordChangeExpanded && (
                <div className="pd-password-change-form">
                  <label>
                    <span>{t("personalData.security.newPassword", { defaultValue: "New password" })}</span>
                    <div className="pd-password-input">
                      <input
                        className="pd-control"
                        type={passwordFieldVisibility.password ? "text" : "password"}
                        autoComplete="new-password"
                        value={passwordChangeForm.password}
                        onChange={(event) => updatePasswordChangeForm("password", event.target.value)}
                      />
                      <button
                        type="button"
                        className="pd-icon-button pd-password-input__toggle"
                        onClick={() => togglePasswordFieldVisibility("password")}
                        aria-label={passwordFieldVisibility.password
                          ? t("personalData.security.hidePassword", { defaultValue: "Hide password" })
                          : t("personalData.security.showPassword", { defaultValue: "Show password" })}
                        title={passwordFieldVisibility.password
                          ? t("personalData.security.hidePassword", { defaultValue: "Hide password" })
                          : t("personalData.security.showPassword", { defaultValue: "Show password" })}
                      >
                        <img src={passwordFieldVisibility.password ? EyeSlashIcon : EyeIcon} alt="" aria-hidden="true" />
                      </button>
                    </div>
                  </label>
                  <label>
                    <span>{t("personalData.security.repeatPassword", { defaultValue: "Repeat password" })}</span>
                    <div className="pd-password-input">
                      <input
                        className="pd-control"
                        type={passwordFieldVisibility.passwordRepeat ? "text" : "password"}
                        autoComplete="new-password"
                        value={passwordChangeForm.passwordRepeat}
                        onChange={(event) => updatePasswordChangeForm("passwordRepeat", event.target.value)}
                      />
                      <button
                        type="button"
                        className="pd-icon-button pd-password-input__toggle"
                        onClick={() => togglePasswordFieldVisibility("passwordRepeat")}
                        aria-label={passwordFieldVisibility.passwordRepeat
                          ? t("personalData.security.hidePassword", { defaultValue: "Hide password" })
                          : t("personalData.security.showPassword", { defaultValue: "Show password" })}
                        title={passwordFieldVisibility.passwordRepeat
                          ? t("personalData.security.hidePassword", { defaultValue: "Hide password" })
                          : t("personalData.security.showPassword", { defaultValue: "Show password" })}
                      >
                        <img src={passwordFieldVisibility.passwordRepeat ? EyeSlashIcon : EyeIcon} alt="" aria-hidden="true" />
                      </button>
                    </div>
                  </label>

                  <p className="pd-password-change-form__hint">
                    {t("personalData.security.passwordRequirements", {
                      defaultValue: "Password must be at least 8 characters long, contain uppercase and lowercase letters, include at least one number and one special character: @ # $ % ^ & + =.",
                    })}
                  </p>

                  {passwordChangeRequested && (
                    <label className="pd-password-change-form__code">
                      <span>
                        {t("personalData.security.passwordCodeText", {
                          email: passwordChangeEmail || authenticatedEmail,
                          defaultValue: `Enter the code sent to ${passwordChangeEmail || authenticatedEmail}.`,
                        })}
                      </span>
                      <input
                        className="pd-control"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        value={passwordChangeCode}
                        onChange={(event) => setPasswordChangeCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                        placeholder={t("personalData.placeholders.confirmationCode", { defaultValue: "Code" })}
                      />
                    </label>
                  )}

                  <div className="pd-password-change-form__actions">
                    {!passwordChangeRequested ? (
                      <button
                        type="button"
                        className={`pd-primary-button ${passwordChangeLoading ? "is-loading" : ""}`}
                        onClick={requestPasswordChange}
                        disabled={passwordChangeLoading}
                      >
                        <span className="pd-button__label">
                          {t("personalData.security.sendPasswordCode", { defaultValue: "Send confirmation code" })}
                        </span>
                      </button>
                    ) : (
                      <>
                        <button
                          type="button"
                          className={`pd-primary-button ${passwordChangeLoading ? "is-loading" : ""}`}
                          onClick={confirmPasswordChange}
                          disabled={passwordChangeLoading || passwordChangeCode.trim().length < 6}
                        >
                          <span className="pd-button__label">
                            {t("personalData.security.confirmPasswordChange", { defaultValue: "Confirm password change" })}
                          </span>
                        </button>
                        <button
                          type="button"
                          className="pd-secondary-button"
                          onClick={requestPasswordChange}
                          disabled={passwordChangeLoading}
                        >
                          {t("personalData.security.resendPasswordCode", { defaultValue: "Send new code" })}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>

      <section className="pd-account-delete-card pd-glass">
        <div>
          <h3>{t("personalData.accountDelete.title", { defaultValue: "Delete ID.Express" })}</h3>
          <p>
            {t("personalData.accountDelete.description", {
              defaultValue:
                "By deleting your ID.Express, you will no longer be able to use these login details on all ID.Express projects: tags.ooo, top100.travel, top100.jobs and others.",
            })}
          </p>
        </div>

        {!accountDeleteRequested ? (
          <button
            type="button"
            className={`pd-danger-button ${accountDeleteLoading ? "is-loading" : ""}`}
            onClick={requestAccountDelete}
            disabled={accountDeleteLoading}
          >
            <span className="pd-button__label">
              {t("personalData.accountDelete.request", { defaultValue: "Delete ID.Express" })}
            </span>
          </button>
        ) : (
          <div className="pd-account-delete-card__confirm">
            <p>
              {t("personalData.accountDelete.codeText", {
                email: accountDeleteEmail || authenticatedEmail,
                defaultValue: `Enter the code sent to ${accountDeleteEmail || authenticatedEmail}.`,
              })}
            </p>
            <div className="pd-account-delete-card__controls">
              <input
                className="pd-control"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={accountDeleteCode}
                onChange={(event) => setAccountDeleteCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder={t("personalData.placeholders.confirmationCode", { defaultValue: "Code" })}
              />
              <button
                type="button"
                className={`pd-danger-button ${accountDeleteLoading ? "is-loading" : ""}`}
                onClick={confirmAccountDelete}
                disabled={accountDeleteLoading || accountDeleteCode.trim().length < 6}
              >
                <span className="pd-button__label">
                  {t("personalData.accountDelete.confirm", { defaultValue: "Confirm deletion" })}
                </span>
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );

  const renderCategoryNav = (className = "") => (
    <section className={`pd-nav-card pd-glass ${className}`.trim()}>
       

      <div className="pd-category-list">
        {topCategories.map((category) => {
          const isActive = activeCategory === category;

          return (
            <button
              key={category}
              type="button"
              className={`pd-category-button ${isActive ? "is-active" : ""}`}
              data-action="select-category"
              data-category={category}
              title={t(`personalData.categories.${category}`, { defaultValue: category })}
            >
              <span>
                {t(`personalData.categories.${category}`, { defaultValue: category })}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );

  return (
    <div
      className={`pd-shell ${theme === "dark" ? "pd-theme-dark" : "pd-theme-light"}`}
      data-theme={theme}
      ref={buttonTitlesRef}
      onClick={handleShellClick}
    >
      <div className="pd-shell__noise" />
      <div className="pd-shell__ambient pd-shell__ambient--one" />
      <div className="pd-shell__ambient pd-shell__ambient--two" />

      <div className="pd-dashboard">
        {draftRestored && (
          <div className="pd-draft-banner pd-glass">
            <div>
              <strong>{t("personalData.ui.draftRecoveredTitle", { defaultValue: "Recovered local draft" })}</strong>
              <p>
                {t("personalData.ui.draftRecoveredText", {
                  defaultValue: "A draft with unsaved changes was found. Continue editing or discard it to return to the latest server data.",
                })}
              </p>
            </div>

            <button type="button" className="pd-draft-clear-button" data-action="clear-draft">
              {t("personalData.ui.clearDraft", { defaultValue: "Discard draft" })}
            </button>
          </div>
        )}

        {notice && <div className={`pd-notice-banner pd-notice-banner--${notice.tone}`}>{notice.text}</div>}

        {pendingEmailChanges.map((pendingEmailChange) => {
          const fieldLabel = pendingEmailChange.fields
            .map((field) => t(`personalData.fields.${field}`, { defaultValue: field }))
            .join(", ");

          return (
            <div className="pd-email-confirm-banner pd-glass" key={`${pendingEmailChange.field}:${pendingEmailChange.email}`}>
              <div>
                <strong>
                  {t("personalData.ui.emailChangeTitleForField", {
                    field: fieldLabel,
                    defaultValue: `Confirm ${fieldLabel}`,
                  })}
                </strong>
                <p>
                  {t("personalData.ui.emailChangeTextForEmail", {
                    email: pendingEmailChange.email,
                    defaultValue: `Enter the code sent to ${pendingEmailChange.email}.`,
                  })}
                </p>
              </div>
              <div className="pd-email-confirm-banner__controls">
                <input
                  className="pd-control"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  value={pendingEmailChange.code}
                  onChange={(event) => {
                    const code = event.target.value.replace(/\D/g, "").slice(0, 6);
                    setPendingEmailChanges((current) =>
                      current.map((item) =>
                        item.email === pendingEmailChange.email ? { ...item, code } : item
                      )
                    );
                  }}
                  placeholder={t("personalData.placeholders.confirmationCode", { defaultValue: "Code" })}
                />
                <button
                  type="button"
                  className={`pd-primary-button ${pendingEmailChange.confirming ? "is-loading" : ""}`}
                  disabled={pendingEmailChange.confirming || pendingEmailChange.code.trim().length < 6}
                  data-action="confirm-email-change"
                  data-email-field={pendingEmailChange.field}
                >
                  {t("personalData.actions.confirm", { defaultValue: "Confirm" })}
                </button>
              </div>
            </div>
          );
        })}

        <section className={`pd-profile-card pd-glass ${savePulse ? "is-pulsing" : ""}`}>
              <div
                className={`pd-avatar-stage ${dragActive ? "is-dragging" : ""}`}
                onDragEnter={(event) => {
                  event.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={(event) => {
                  event.preventDefault();
                  setDragActive(false);
                }}
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragActive(false);
                  processAvatarFile(event.dataTransfer.files?.[0]);
                }}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(event) => processAvatarFile(event.target.files?.[0])}
                />

                <div className={`pd-avatar-preview ${accountIsVerified ? "is-account-verified" : ""}`}>
                  <button
                    type="button"
                    className={`pd-account-verified-badge ${accountIsVerified ? "is-verified" : "is-muted"}`}
                    data-action="open-profile-verification-section"
                    title={t(
                      accountIsVerified
                        ? "personalData.accountVerification.verifiedTitle"
                        : "personalData.accountVerification.notSubmittedTitle",
                      {
                        defaultValue: accountIsVerified ? "Verified account" : "Account is not verified",
                      }
                    )}
                    aria-label={t(
                      accountIsVerified
                        ? "personalData.accountVerification.verifiedTitle"
                        : "personalData.accountVerification.notSubmittedTitle",
                      {
                        defaultValue: accountIsVerified ? "Verified account" : "Account is not verified",
                      }
                    )}
                  >
                    ✓
                  </button>
                  <div className="pd-avatar-image-frame">
                    {avatarPreview ? (
                      <img
                        src={avatarPreview}
                        alt={t("personalData.avatar.noAvatar")}
                      />
                    ) : (
                      <div className="pd-avatar-empty">
                        <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                          <path d="M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm0 2c-5.2 0-8 2.6-8 5.2 0 1 .8 1.8 1.8 1.8h12.4c1 0 1.8-.8 1.8-1.8 0-2.6-2.8-5.2-8-5.2Z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="pd-avatar-camera"
                    data-action="open-file-picker"
                    title={t("personalData.ui.chooseImage", { defaultValue: "Choose image" })}
                    aria-label={t("personalData.ui.chooseImage", { defaultValue: "Choose image" })}
                  >
                    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <path d="M9 4.5 7.6 6H5a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V9a3 3 0 0 0-3-3h-2.6L15 4.5H9Zm3 12.75A4.25 4.25 0 1 1 12 8.75a4.25 4.25 0 0 1 0 8.5Zm0-1.75a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="pd-profile-card__header">
                <div>
                  <h2>{displayName}</h2>
                </div>
              </div>

              {avatarPreview && avatarFile && (
                <button
                  type="button"
                  className={`pd-primary-button pd-avatar-save ${avatarUploading ? "is-loading" : ""}`}
                  onClick={handleAvatarUpload}
                  disabled={avatarUploading}
                >
                  <span className="pd-button__label">
                    {avatarUploading
                      ? t("personalData.loading.avatar")
                      : t("personalData.actions.updateAvatar")}
                  </span>
                </button>
              )}
 

              
        </section>

        <div className="pd-layout">
          <aside className="pd-sidebar">
            <div className="pd-sidebar__nav">
              {renderCategoryNav()}
            </div>
          </aside>

          <main className="pd-main">
            <section ref={activeSectionRef} tabIndex={-1} className="pd-form-shell pd-glass pd-active-section">
              <div className="pd-form-shell__header">
                <div>
                  
                  <h2>
                    {t(`personalData.categories.${activeCategory}`, { defaultValue: activeCategory })}
                  </h2>
                
                </div>

                <div className="pd-inline-metrics">
                  <span className="pd-inline-chip">
                    {t("personalData.ui.autosave", { defaultValue: "Auto-save" })}
                     <div className={`pd-status-chip pd-status-chip--${saveState}`} title={`${statusLabel}`}>
              <span className="pd-status-chip__dot" />
            </div>
                  </span>
                 
                </div>
              </div>

              {loading ? (
                <div className="pd-shimmer-stack" aria-hidden="true">
                  <div className="pd-shimmer pd-shimmer--hero" />
                  <div className="pd-shimmer pd-shimmer--line" />
                  <div className="pd-shimmer pd-shimmer--line" />
                  <div className="pd-shimmer pd-shimmer--line short" />
                </div>
              ) : activeCategory === "security" ? (
                renderSecuritySection()
              ) : (
                <DynamicFormWithNestedTabs
                  key={activeCategory}
                  data={{ [activeCategory]: activeCategoryData }}
                  onSubmit={(submittedData) => handleSubmit(submittedData, activeCategory)}
                  onDataChange={handleFormDataChange}
                  requestHeaders={authHeaders}
                  isEmptyState={categoryIsEmpty}
                  submitState={saveState}
                  syncVersion={formVersion}
                  fieldMetadata={{ [activeCategory]: fieldMetadata[activeCategory] ?? {} }}
                />
              )}
            </section>

            {activeCategory === "profile" && (
              <div id="pd-profile-verification" className={`pd-account-verification-card pd-glass ${accountIsVerified ? "is-verified" : accountVerificationHasRequest ? "is-pending" : ""}`}>
                <div className="pd-account-verification-card__header">
                  <span className={`pd-account-verification-card__icon ${accountIsVerified ? "is-verified" : "is-muted"}`}>✓</span>
                  <div>
                    <strong>
                      {t(
                        accountIsVerified
                          ? "personalData.accountVerification.verifiedTitle"
                          : accountVerificationHasRequest
                            ? "personalData.accountVerification.pendingTitle"
                            : "personalData.accountVerification.notSubmittedTitle",
                        {
                          defaultValue: accountIsVerified
                            ? "Verified account"
                            : accountVerificationHasRequest
                              ? "Sent for review"
                              : "Not sent for review",
                        }
                      )}
                    </strong>
                    <p>
                      {t(
                        accountIsVerified
                          ? "personalData.accountVerification.verifiedDescription"
                          : "personalData.accountVerification.description",
                        {
                          defaultValue: accountIsVerified
                            ? "The account is verified and has passed identity verification."
                            : "Verified account status means that the user provided personal data and confirmed their identity to the administration.",
                        }
                      )}
                    </p>
                  </div>
                </div>

                {!accountIsVerified && (
                  <button type="button" className="pd-secondary-button pd-account-verification-card__toggle" data-action="toggle-profile-verification" aria-expanded={showProfileVerificationForm}>
                    {t(
                      showProfileVerificationForm
                        ? "personalData.accountVerification.hideForm"
                        : "personalData.accountVerification.getStatus",
                      { defaultValue: showProfileVerificationForm ? "Hide form" : "Get status" }
                    )}
                  </button>
                )}

                {showProfileVerificationForm && (
                  <div className="pd-account-verification-card__body">
                    <div className="pd-selfie-instruction">
                      <div className="pd-selfie-example" aria-hidden="true">
                        <ProfileVerificationIcon className="pd-selfie-example__icon" />
                      </div>

                      <div className="pd-account-verification-card__copy">
                        <strong>{t("personalData.accountVerification.selfieTitle", { defaultValue: "Take a selfie with your passport" })}</strong>
                        <p>
                          {t("personalData.accountVerification.selfieText", {
                            defaultValue:
                              "Hold your passport in your hand. Your first name, last name and passport photo must be readable. Other information is optional.",
                          })}
                        </p>
                      </div>
                    </div>

                    <input
                      ref={profileVerificationInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      hidden
                      onChange={(event) => processProfileVerificationFile(event.target.files?.[0])}
                    />
                    <button type="button" className="pd-verification-upload" data-action="open-profile-verification-file-picker">
                      <span className="pd-verification-upload__icon">↑</span>
                      <span>
                        {accountVerificationFileName ||
                          t("personalData.accountVerification.uploadPrompt", {
                            defaultValue: "Choose or drag a photo here",
                          })}
                      </span>
                      <small>{t("personalData.accountVerification.maxSize", { defaultValue: "Photo size must not exceed 10 MB" })}</small>
                    </button>
                    <small className="pd-account-verification-card__hint">
                      {t("personalData.accountVerification.copyrightNotice", {
                        defaultValue: "By uploading, you confirm that you own the rights to the file.",
                      })}
                    </small>

                    <label className="pd-account-verification-consent">
                      <input
                        type="checkbox"
                        checked={profileVerificationConsent}
                        onChange={(event) => {
                          setProfileVerificationConsent(event.target.checked);
                          if (event.target.checked) setProfileVerificationValidationVisible(false);
                        }}
                      />
                      <span>
                        {t("personalData.accountVerification.consentText", {
                          defaultValue:
                            "I agree to provide personal data to receive Verified account status.",
                        })}{" "}
                        <a href={verifyPath}>
                          {t("personalData.accountVerification.learnMore", { defaultValue: "Learn more" })}
                        </a>
                      </span>
                    </label>

                    {profileVerificationValidationVisible && !profileVerificationCanSubmit && (
                      <div className="pd-account-verification-validation" role="alert">
                        <strong>
                          {t("personalData.accountVerification.validationSummary", {
                            defaultValue: "Complete the required fields before sending the request.",
                          })}
                        </strong>
                        <ul>
                          {profileVerificationMissingFields.map((field) => (
                            <li key={field}>{field}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <button
                      type="button"
                      className={`pd-primary-button ${profileVerificationSubmitting ? "is-loading" : ""} ${!profileVerificationCanSubmit ? "is-disabled" : ""}`}
                      onClick={submitProfileVerification}
                      aria-disabled={!profileVerificationCanSubmit || profileVerificationSubmitting}
                    >
                      <span className="pd-button__label">
                        {t("personalData.accountVerification.submit", { defaultValue: "Send for review" })}
                      </span>
                    </button>
                  </div>
                )}
              </div>
            )}


          </main>
        </div>

        <div
          id="pd-mobile-nav"
          className={`pd-mobile-nav ${isMobileNavOpen ? "is-open" : ""}`}
          aria-hidden={!isMobileNavOpen}
        >
          <button
            type="button"
            className="pd-mobile-nav__backdrop"
            aria-label={t("personalData.ui.closeMenu", { defaultValue: "Close menu" })}
            onClick={() => setIsMobileNavOpen(false)}
          />

          <div
            className="pd-mobile-nav__panel"
            role="dialog"
            aria-modal="true"
            aria-label={t("personalData.ui.navigateData", { defaultValue: "Navigate your data" })}
          >
            <div className="pd-mobile-nav__panel-header">
              <button
                type="button"
                className="pd-mobile-nav__close"
                onClick={() => setIsMobileNavOpen(false)}
                aria-label={t("personalData.ui.closeMenu", { defaultValue: "Close menu" })}
              >
                <span className="pd-mobile-nav__close-line" aria-hidden="true" />
                <span className="pd-mobile-nav__close-line" aria-hidden="true" />
              </button>
            </div>

            <div className="pd-mobile-nav__content">
              {renderCategoryNav("pd-mobile-nav__card")}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PersonalData;
